import { Draggable } from '@hello-pangea/dnd';
import { Box, Card, Stack, Typography } from '@mui/material';
import { useRedirect } from 'react-admin';
import { MissionLight } from '@wwf/sdk';
import { MemberLightCard } from '@app/member/show/MemberLightCard';
import { MissionTypeChip } from '../chips/MissionTypeChip';
import { MissionDateChip } from '../chips/MissionDateChip';
import { AntennaLightCard } from '@app/antenna/show/AntennaLightCard';
import { MemberIcon } from '@app/member/MemberIcon';
import { MediaIcon } from '@app/media/MediaIcon';
import { ReportIcon } from '../ReportIcon';
import { MissionVisibilityIcon } from '../MissionVisibilityIcon';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { SessionCount } from '../chips/SessionCount';

export const MissionCard = ({
    mission,
    index,
}: {
    mission: MissionLight;
    index: number;
}) => {
    if (!mission) return null;

    return (
        <Draggable draggableId={String(mission.id)} index={index}>
            {(provided, snapshot) => (
                <MissionCardContent
                    provided={provided}
                    snapshot={snapshot}
                    mission={mission}
                />
            )}
        </Draggable>
    );
};

export const MissionCardContent = ({
    provided,
    snapshot,
    mission,
}: {
    provided?: any;
    snapshot?: any;
    mission: MissionLight;
}) => {
    const redirect = useRedirect();
    const handleClick = () => {
        redirect(
            `/mission/${mission.id}/show`,
            undefined,
            undefined,
            undefined,
            {
                _scrollToTop: false,
            }
        );
    };

    return (
        <Box
            sx={{ marginBottom: 1, cursor: 'pointer' }}
            {...provided?.draggableProps}
            {...provided?.dragHandleProps}
            ref={provided?.innerRef}
            onClick={handleClick}
        >
            <Card
                style={{
                    opacity: snapshot?.isDragging ? 0.9 : 1,
                    transform: snapshot?.isDragging ? 'rotate(-2deg)' : '',
                }}
                elevation={snapshot?.isDragging ? 3 : 1}
            >
                <Box padding={1} display="flex">
                    <Box sx={{ marginLeft: 1 }}>
                        <Typography variant="body2" gutterBottom>
                            {mission.name}
                        </Typography>
                        <Stack
                            direction="column"
                            alignItems="flex-start"
                            gap={1}
                        >
                            <MissionDateChip date={mission.startDate} />
                            <AntennaLightCard
                                record={mission.antenna ?? undefined}
                                noLink
                            />
                            <MemberLightCard
                                record={mission.contact ?? undefined}
                                noLink
                            />
                            <MissionTypeChip type={mission.type} />
                            <Stack
                                direction="row"
                                gap={1}
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                            >
                                {mission.whoCanSee && (
                                    <MissionVisibilityIcon
                                        visibility={mission.whoCanSee}
                                    />
                                )}
                                <SessionCount count={mission.nbSessions ?? 0} />
                                {(mission.participantIds?.length ?? 0) > 0 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.2,
                                        }}
                                    >
                                        <Typography variant="subtitle2">
                                            {mission.participantIds?.length ??
                                                0}
                                        </Typography>
                                        <MemberIcon />
                                    </Box>
                                )}

                                {(mission.nbMedias ?? 0) > 0 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.2,
                                        }}
                                    >
                                        <Typography variant="subtitle2">
                                            {mission.nbMedias ?? 0}
                                        </Typography>
                                        <MediaIcon />
                                    </Box>
                                )}

                                {(mission.totalTouchedPersonCount ?? 0) > 0 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.2,
                                        }}
                                    >
                                        <Typography variant="subtitle2">
                                            {mission.totalTouchedPersonCount}
                                        </Typography>
                                        <PeopleOutlineIcon />
                                    </Box>
                                )}

                                {mission.reportSentDate ? <ReportIcon /> : null}
                            </Stack>
                        </Stack>
                    </Box>
                </Box>
            </Card>
        </Box>
    );
};
